import { Link, useParams } from 'react-router-dom';

import { Card, Empty, EntityChip, StatTile, StatusPill } from '../components/ui';
import { useApi } from '../lib/useApi';

interface ClientRow {
  id: string;
  code: string;
  name: string;
  slug: string;
  isInternal: boolean;
  notes: string | null;
  deployments: number;
  servers: number;
  containers: number;
}

export function Clients() {
  const { data, error, loading } = useApi<ClientRow[]>('/clients');

  if (loading && !data) return <p className="empty">Loading clients…</p>;
  if (error && !data) return <p className="form-error">{error}</p>;

  return (
    <>
      <div className="page-head">
        <h1>Clients</h1>
      </div>

      <div className="grid-cards">
        {(data ?? []).map((c) => (
          <Card
            key={c.id}
            title={<Link to={`/clients/${c.id}`}>{c.name}</Link>}
            actions={<EntityChip type="client" label={c.code} />}
          >
            {c.isInternal && <p className="notice">Internal — AdeptivIQ&apos;s own estate.</p>}
            <div className="tiles tiles-sm">
              <StatTile label="Deployments" value={c.deployments} />
              <StatTile label="Containers" value={c.containers} />
            </div>
            {c.notes && <p className="muted small">{c.notes}</p>}
          </Card>
        ))}
      </div>
    </>
  );
}

interface ClientDetailData {
  id: string;
  code: string;
  name: string;
  notes: string | null;
  deployments: Array<{
    id: string;
    name: string;
    version: string | null;
    versionSource: string | null;
    isDeclared: boolean;
    needsReview: boolean;
    product: { key: string; name: string; isInfrastructure: boolean };
    server: { id: string; name: string; hostname: string; status: string };
    containers: Array<{ id: string; name: string; state: string; version: string | null }>;
  }>;
}

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, error, loading } = useApi<ClientDetailData>(id ? `/clients/${id}` : null);

  if (loading && !data) return <p className="empty">Loading…</p>;
  if (error && !data) return <p className="form-error">{error}</p>;
  if (!data) return null;

  const products = data.deployments.filter((d) => !d.product.isInfrastructure);
  const infra = data.deployments.filter((d) => d.product.isInfrastructure);

  // A client's servers are the ones carrying its deployments, which is not the
  // same as the ones it owns: ABC Corporation runs FrontDeskIQ on a Whitecloud
  // Software box, and that server belongs on ABC's page too.
  const servers = new Map(data.deployments.map((d) => [d.server.id, d.server]));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{data.name}</h1>
          {data.notes && <p className="muted">{data.notes}</p>}
        </div>
        <EntityChip type="client" label={data.code} />
      </div>

      <div className="tiles">
        <StatTile label="Products" value={products.length} />
        <StatTile label="Servers" value={servers.size} />
        <StatTile
          label="Containers"
          value={data.deployments.reduce((n, d) => n + d.containers.length, 0)}
        />
      </div>

      <Card title="Deployments">
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th>Product</th><th>Version</th><th>Server</th><th className="num">Containers</th></tr>
            </thead>
            <tbody>
              {products.map((d) => (
                <tr key={d.id}>
                  <td>
                    <EntityChip type="product" label={d.product.name} />
                    {/* A stack the collector found rather than one anybody declared.
                        Its client is the server owner by default, which is a
                        guess — so it says so until a person confirms it. */}
                    {d.needsReview && (
                      <span className="tag tag-warn" title="Discovered by the collector; client inherited from the server owner">
                        needs review
                      </span>
                    )}
                  </td>
                  <td>
                    {d.version ? (
                      <span className="ver">{d.version}</span>
                    ) : (
                      // Never invent a version. A stack built from an untagged
                      // image has no version to report, and "latest" is not one.
                      <span className="muted" title="No version-shaped image tag on this stack">
                        not versioned
                      </span>
                    )}
                  </td>
                  <td>
                    <Link to={`/servers/${d.server.id}`}>{d.server.name}</Link>{' '}
                    <StatusPill status={d.server.status} />
                  </td>
                  <td className="num">{d.containers.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {products.length === 0 && <Empty>No product deployments recorded for this client.</Empty>}
      </Card>

      {infra.length > 0 && (
        <Card title="Supporting infrastructure">
          <ul className="chips">
            {infra.map((d) => (
              <li key={d.id}>
                {d.product.name}
                <span className="chip-client">{d.server.name}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
